import { DateTime } from "luxon";
import { useSelector } from "react-redux";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

import { getUserIsSnackpassEmployee } from "src/redux/selectors";
import { OrderField } from "#orders/lib";
import { Skeleton } from "src/@/components/ui/skeleton";
import { PaginatedDataTable } from "src/@/components/ui/data-table";

import { OrdersDownloadButton } from "../download-button";
import { useBridgeEnhancedOrders } from "../../hooks/use-bridge-enhanced-orders";
import { useOrders } from "../../orders-context";

import { useOrderColumns } from "./columns";

export const OrdersTable = () => {
    const {
        purchaseReportRows: { data: rows, isLoading },
        isBridgeDataUsed,
    } = useBridgeEnhancedOrders();
    const { pagination, setPagination, sorting, setSorting, globalFilter, setGlobalFilter } = useOrders();
    const isSnackTeam = useSelector(getUserIsSnackpassEmployee);
    const updatedTime = DateTime.now().toFormat("h:mm a");
    const toolbarOptions = {
        showColumnFilter: true,
        search: {
            global: true as const,
            placeholder: t`Search by order #, customer name, phone`,
        },
        customComponent: <OrdersDownloadButton />,
    } as const;

    const columns = useOrderColumns(isSnackTeam, isBridgeDataUsed);

    const getRowClassName = () => undefined;

    if (isLoading) {
        return (
            <>
                <Skeleton className="my-4 h-8 w-64" />
                <Skeleton className="h-52 w-full" />
            </>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h4 className="text-lg">
                    <Trans>Orders</Trans>
                </h4>
            </div>
            <PaginatedDataTable
                toolbar={toolbarOptions}
                columns={columns}
                data={rows ?? []}
                showPagination={true}
                tableId="orders-table"
                getRowClassName={getRowClassName}
                pagination={pagination}
                setPagination={setPagination}
                sorting={sorting}
                setSorting={setSorting}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                hiddenColumns={[
                    OrderField.CustomerPhone,
                    OrderField.CustomSurcharge,
                    OrderField.CustomDiscounts,
                    OrderField.TaxesRemitted,
                    OrderField.PaymentMethod,
                    OrderField.Items,
                    OrderField.UpCharge,
                    OrderField.DeliveryCharge,
                    OrderField.ThirdPartyDeliveryCharge,
                    OrderField.NetSales,
                    OrderField.GrossSales,
                    OrderField.RefundedBy,
                    OrderField.Promotions,
                    OrderField.Notes,
                    OrderField.UpChargedBy,
                    OrderField.Channel,
                    OrderField.Provider,
                    OrderField.ProcessingFee,
                    OrderField.ThirdPartyPayout,
                    OrderField.NetPayout,
                    OrderField.SnackpassFee,
                    OrderField.ID,
                    OrderField.PayoutID,
                    OrderField.PaidOutOn,
                    OrderField.ActualPayout,
                    OrderField.CashAcceptedBy,
                    OrderField.ThirdPartyFees,
                    OrderField.ThirdPartyTaxes,
                    OrderField.CreatedBy,
                ]}
                footerElement={
                    <span className="text-xs text-muted-foreground">
                        <Trans>Updated</Trans> {updatedTime}
                    </span>
                }
            />
        </div>
    );
};
